import React, { useEffect, useState, useContext } from 'react';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Users, Shield, Settings, Trash2 } from 'lucide-react';

export default function GroupsManagementPage() {
  const { user } = useContext(AuthContext);
  const [groups, setGroups] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupRoles, setGroupRoles] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [rolesDialogOpen, setRolesDialogOpen] = useState(false);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [newGroupData, setNewGroupData] = useState({ name: '', description: '' });

  useEffect(() => {
    fetchGroups();
    fetchAllRoles();
    fetchAllUsers();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get('/admin/groups');
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch groups', error);
      toast.error('Errore nel caricamento gruppi');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllRoles = async () => {
    try {
      const response = await axios.get('/admin/roles');
      setAllRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch roles', error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get('/admin/users');
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users', error);
    }
  };

  const fetchGroupRoles = async (groupId) => {
    try {
      const response = await axios.get(`/admin/groups/${groupId}/roles`);
      setGroupRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch group roles', error);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/admin/groups', null, {
        params: { 
          name: newGroupData.name, 
          description: newGroupData.description 
        }
      });
      toast.success('Gruppo creato con successo!');
      setCreateDialogOpen(false);
      setNewGroupData({ name: '', description: '' });
      fetchGroups();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella creazione gruppo'));
    }
  };

  const handleOpenRolesDialog = async (group) => {
    setSelectedGroup(group);
    await fetchGroupRoles(group.id);
    setRolesDialogOpen(true);
  };

  const handleToggleRole = async (roleId, isAssigned) => {
    try {
      if (isAssigned) {
        await axios.delete(`/admin/groups/${selectedGroup.id}/roles/${roleId}`);
        toast.success('Ruolo rimosso');
      } else {
        await axios.post(`/admin/groups/${selectedGroup.id}/roles/${roleId}`);
        toast.success('Ruolo assegnato');
      }
      await fetchGroupRoles(selectedGroup.id);
      fetchGroups();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'operazione'));
    }
  };

  const handleAssignUserToGroup = async (userId, groupId) => {
    try {
      await axios.put(`/admin/users/${userId}/group`, null, {
        params: { group_id: groupId }
      });
      toast.success('Utente assegnato al gruppo');
      fetchAllUsers();
      fetchGroups();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'assegnazione'));
    }
  };

  const isRoleAssigned = (roleId) => {
    return groupRoles.some(r => r.id === roleId);
  };



  return (
    <div className="min-h-screen bg-white" data-testid="groups-management-page">
      <Navbar />

      <div className="container mx-auto px-4 sm:px-6 pt-20 sm:pt-24 pb-8 sm:pb-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 sm:mb-12 gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2" data-testid="page-title">
              Gestione Gruppi e Ruoli
            </h1>
            <p className="text-gray-600">Configura permessi e gruppi utenti</p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-black text-white hover:bg-gray-800 w-full sm:w-auto">
                + Nuovo Gruppo
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crea Nuovo Gruppo</DialogTitle>
                <DialogDescription>
                  Crea un gruppo e assegna i ruoli necessari
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateGroup} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome Gruppo *</Label>
                  <Input
                    id="name"
                    required
                    value={newGroupData.name}
                    onChange={(e) => setNewGroupData({...newGroupData, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="description">Descrizione</Label>
                  <Textarea
                    id="description"
                    rows={3}
                    value={newGroupData.description}
                    onChange={(e) => setNewGroupData({...newGroupData, description: e.target.value})}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                    Crea Gruppo
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12">Caricamento...</div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groups.map((group) => (
                <Card key={group.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-xl mb-2">{group.name}</CardTitle>
                        <CardDescription className="text-sm">
                          {group.description}
                        </CardDescription>
                      </div>
                      <Users className="w-5 h-5 text-gray-500" />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Utenti:</span>
                        <span className="font-semibold">{group.user_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Ruoli:</span>
                        <span className="font-semibold">{group.role_count}</span>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4 border-t">
                      <Button 
                        className="flex-1 bg-black text-white hover:bg-gray-800"
                        onClick={() => handleOpenRolesDialog(group)}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Gestisci Ruoli
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setSelectedGroup(group);
                          setUsersDialogOpen(true);
                        }}
                      >
                        <Users className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Roles Management Dialog */}
            <Dialog open={rolesDialogOpen} onOpenChange={setRolesDialogOpen}>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Gestisci Ruoli - {selectedGroup?.name}</DialogTitle>
                  <DialogDescription>
                    Seleziona i ruoli da assegnare a questo gruppo
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                  {allRoles.map((role) => {
                    const isAssigned = isRoleAssigned(role.id);
                    return (
                      <div 
                        key={role.id} 
                        className="flex items-start space-x-3 p-3 border rounded hover:bg-gray-50"
                      >
                        <Checkbox
                          id={role.id}
                          checked={isAssigned}
                          onCheckedChange={() => handleToggleRole(role.id, isAssigned)}
                        />
                        <div className="flex-1">
                          <Label 
                            htmlFor={role.id} 
                            className="font-semibold cursor-pointer"
                          >
                            {role.code}
                          </Label>
                          <p className="text-sm text-gray-600">{role.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </DialogContent>
            </Dialog>

            {/* Users Assignment Dialog */}
            <Dialog open={usersDialogOpen} onOpenChange={setUsersDialogOpen}>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Utenti - {selectedGroup?.name}</DialogTitle>
                  <DialogDescription>
                    Assegna utenti a questo gruppo
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                  {allUsers.map((u) => (
                      <div 
                        key={u.id} 
                        className="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                      >
                        <div className="flex-1">
                          <p className="font-semibold">
                            {u.first_name} {u.last_name}
                          </p>
                          <p className="text-sm text-gray-600">{u.email}</p>
                          {u.group_id && (
                            <p className="text-xs text-gray-500">
                              Gruppo attuale: {groups.find(g => g.id === u.group_id)?.name}
                            </p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant={u.group_id === selectedGroup?.id ? "outline" : "default"}
                          onClick={() => handleAssignUserToGroup(u.id, selectedGroup?.id)}
                          disabled={u.group_id === selectedGroup?.id}
                        >
                          {u.group_id === selectedGroup?.id ? 'Assegnato' : 'Assegna'}
                        </Button>
                      </div>
                    ))}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
    </div>
  );
}
